<template>
    <UserLayout :page-title="t('ticketDetails')" :page-section="t('support')">
        <div class="sts-wrap">

            <!-- Back -->
            <button @click="router.visit('/user/support-tickets')" class="sts-back anim-up" style="--d:0ms">
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                </svg>
                {{ t('backToTickets') }}
            </button>

            <!-- Ticket header -->
            <div class="sts-card anim-up" style="--d:40ms">

                <div class="sts-hdr">
                    <div class="sts-hdr-main">
                        <div class="sts-hdr-badges">
                            <span class="sts-badge"
                                :style="`background:${statusConfig[ticket.status]?.bgColor};color:${statusConfig[ticket.status]?.color}`">
                                {{ t(statusConfig[ticket.status]?.labelKey) || ticket.status }}
                            </span>
                            <span class="sts-badge"
                                :style="`background:rgba(${hexToRgb(priorityConfig[ticket.priority]?.color || '#6b7280').r},${hexToRgb(priorityConfig[ticket.priority]?.color || '#6b7280').g},${hexToRgb(priorityConfig[ticket.priority]?.color || '#6b7280').b},0.12);color:${priorityConfig[ticket.priority]?.color || '#6b7280'}`">
                                {{ t(priorityConfig[ticket.priority]?.labelKey) || ticket.priority }}
                            </span>
                        </div>
                        <h1 class="sts-subject">{{ ticket.subject }}</h1>
                        <div class="sts-meta">
                            <span class="sts-meta-item">
                                <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/>
                                </svg>
                                {{ ticket.ticket_number }}
                            </span>
                            <span class="sts-meta-item">
                                <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                {{ formatDate(ticket.created_at) }}
                            </span>
                            <span v-if="ticket.category" class="sts-meta-item">
                                <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
                                </svg>
                                {{ ticket.category }}
                            </span>
                        </div>
                    </div>
                    <div v-if="ticket.assigned_to" class="sts-assigned">
                        <div class="sts-assigned-avatar">{{ (ticket.assigned_to.display_name || ticket.assigned_to.name)?.charAt(0) || '?' }}</div>
                        <div>
                            <p class="sts-assigned-label">{{ t('assignedTo') }}</p>
                            <p class="sts-assigned-name">{{ ticket.assigned_to.display_name || ticket.assigned_to.name }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Description -->
            <div class="sts-card anim-up" style="--d:80ms">

                <div class="sts-section-title">{{ t('description') }}</div>
                <p class="sts-desc-text">{{ ticket.description }}</p>

                <div v-if="ticket.attachments && ticket.attachments.length" class="sts-attachments">
                    <p class="sts-attach-label">{{ t('attachments') }}</p>
                    <div class="sts-attach-grid">
                        <div v-for="attachment in ticket.attachments" :key="attachment.id"
                            class="sts-attach-item" @click="downloadAttachment(attachment.id)">
                            <div class="sts-attach-icon">
                                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                                </svg>
                            </div>
                            <div class="sts-attach-info">
                                <p class="sts-attach-name">{{ attachment.original_name }}</p>
                                <p class="sts-attach-size">{{ formatFileSize(attachment.file_size) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Conversation -->
            <div class="sts-conv-head anim-up" style="--d:110ms">{{ t('conversation') }}</div>

            <!-- Replies -->
            <template v-if="ticket.replies && ticket.replies.length">
                <div v-for="reply in ticket.replies" :key="reply.id"
                    class="sts-reply anim-up"
                    :class="reply.is_admin_reply ? 'sts-reply--admin' : ''"
                    style="--d:130ms">
    
                    <div class="sts-reply-hdr">
                        <div class="sts-reply-avatar"
                            :style="reply.is_admin_reply ? 'background:linear-gradient(135deg,#a75ded,#7c3aed)' : 'background:#64748b'">
                            {{ (reply.user.display_name || reply.user.name)?.charAt(0) || '?' }}
                        </div>
                        <div class="sts-reply-meta">
                            <div class="sts-reply-name-row">
                                <span class="sts-reply-name">{{ reply.user.display_name || reply.user.name }}</span>
                                <span v-if="reply.is_admin_reply" class="sts-support-tag">{{ t('supportTeam') }}</span>
                                <span class="sts-reply-time">{{ formatDate(reply.created_at) }}</span>
                            </div>
                            <p class="sts-reply-msg">{{ reply.message }}</p>

                            <div v-if="reply.attachments && reply.attachments.length" class="sts-reply-attach">
                                <div v-for="attachment in reply.attachments" :key="attachment.id"
                                    class="sts-attach-item sts-attach-item--sm" @click="downloadAttachment(attachment.id)">
                                    <div class="sts-attach-icon sts-attach-icon--sm">
                                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                                        </svg>
                                    </div>
                                    <div>
                                        <p class="sts-attach-name">{{ attachment.original_name }}</p>
                                        <p class="sts-attach-size">{{ formatFileSize(attachment.file_size) }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <div v-else class="sts-card sts-empty anim-up" style="--d:130ms">

                <div class="sts-empty-icon">
                    <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                </div>
                <p class="sts-empty-text">{{ t('noRepliesYet') }}</p>
            </div>

            <!-- Reply form -->
            <div v-if="ticket.status !== 'closed'" class="sts-card anim-up" style="--d:160ms">

                <div class="sts-section-title">{{ t('addReply') }}</div>
                <form @submit.prevent="submitReply" class="sts-form">
                    <div class="sts-field">
                        <label class="sts-field-label">{{ t('yourMessage') }}</label>
                        <textarea v-model="replyForm.message" rows="5"
                            :placeholder="t('typeYourReplyHere')"
                            :class="['sts-input sts-textarea', replyForm.errors.message ? 'sts-input--err' : '']"></textarea>
                        <p v-if="replyForm.errors.message" class="sts-err">{{ replyForm.errors.message }}</p>
                    </div>

                    <div class="sts-field">
                        <label class="sts-field-label">{{ t('attachmentsOptional') }}</label>
                        <input ref="fileInput" type="file" multiple
                            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.txt,.zip"
                            @change="handleFileSelect" class="sts-hidden" />
                        <button type="button" @click="fileInput.click()" class="sts-attach-btn">
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
                            </svg>
                            {{ t('addFiles') }}
                        </button>

                        <div v-if="selectedFiles.length" class="sts-files">
                            <div v-for="(file, index) in selectedFiles" :key="index" class="sts-file-item">
                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color:rgba(167,93,237,0.7);flex-shrink:0">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                                </svg>
                                <div class="sts-file-info">
                                    <p class="sts-file-name">{{ file.name }}</p>
                                    <p class="sts-file-size">{{ formatFileSize(file.size) }}</p>
                                </div>
                                <button type="button" @click="removeFile(index)" class="sts-file-remove">
                                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <button type="submit" :disabled="replyForm.processing || !replyForm.message" class="sts-submit">
                        <span v-if="replyForm.processing">{{ t('sending') }}...</span>
                        <span v-else>{{ t('sendReply') }}</span>
                    </button>
                </form>
            </div>

            <!-- Closed ticket -->
            <div v-else class="sts-card sts-closed anim-up" style="--d:160ms">

                <div class="sts-empty-icon">
                    <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                    </svg>
                </div>
                <p class="sts-empty-text">{{ t('ticketClosedMessage') }}</p>
            </div>

        </div>
    </UserLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'
import { useToast } from "@/composables/useToast.js"
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue"

const props = defineProps({
    ticket: {
        type: Object,
        required: true
    }
})

const page = usePage()
const { t } = useTranslation()
const { showToast } = useToast()

const fileInput = ref(null)
const selectedFiles = ref([])

const replyForm = ref({
    message: '',
    attachments: [],
    processing: false,
    errors: {}
})

const primaryColor = computed(() => page.props.primaryColor || '#86efac')

const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 134, g: 239, b: 172 }
}

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex)
    const newR = Math.min(255, Math.max(0, rgb.r + amount))
    const newG = Math.min(255, Math.max(0, rgb.g + amount))
    const newB = Math.min(255, Math.max(0, rgb.b + amount))
    return "#" + ((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1)
}

const themeColors = computed(() => {
    const rgb = hexToRgb(primaryColor.value)
    return {
        primary: primaryColor.value,
        primaryDark: adjustColor(primaryColor.value, -20),
        primaryRgb: `${rgb.r}, ${rgb.g}, ${rgb.b}`
    }
})

const statusConfig = {
    open: {
        labelKey: 'statusOpen',
        bgColor: '#3b82f620',
        color: '#3b82f6'
    },
    in_progress: {
        labelKey: 'statusInProgress',
        bgColor: '#f59e0b20',
        color: '#f59e0b'
    },
    resolved: {
        labelKey: 'statusResolved',
        bgColor: '#10b98120',
        color: '#10b981'
    },
    closed: {
        labelKey: 'statusClosed',
        bgColor: '#64748b20',
        color: '#64748b'
    }
}

const priorityConfig = {
    low: {
        labelKey: 'priorityLow',
        color: '#64748b'
    },
    medium: {
        labelKey: 'priorityMedium',
        color: '#f59e0b'
    },
    high: {
        labelKey: 'priorityHigh',
        color: '#ef4444'
    },
    urgent: {
        labelKey: 'priorityUrgent',
        color: '#dc2626'
    }
}

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

const handleFileSelect = (event) => {
    const files = Array.from(event.target.files)
    const maxSize = 5 * 1024 * 1024
    const invalidFiles = files.filter(file => file.size > maxSize)

    if (invalidFiles.length > 0) {
        showToast(t('fileSizeTooLarge') || 'Some files exceed the maximum size of 5MB', 'error')
        return
    }

    selectedFiles.value = [...selectedFiles.value, ...files]
    replyForm.value.attachments = selectedFiles.value
    showToast(t('filesSelected') || `${files.length} file(s) selected`, 'success')
}

const removeFile = (index) => {
    selectedFiles.value.splice(index, 1)
    replyForm.value.attachments = selectedFiles.value
    showToast(t('fileRemoved') || 'File removed', 'info')
}

const submitReply = () => {
    if (!replyForm.value.message.trim()) {
        showToast(t('messageRequired') || 'Please enter a message', 'error')
        return
    }

    replyForm.value.processing = true
    replyForm.value.errors = {}

    const formData = new FormData()
    formData.append('message', replyForm.value.message)

    selectedFiles.value.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file)
    })

    router.post(`/user/support-tickets/${props.ticket.id}/reply`, formData, {
        preserveScroll: true,
        onSuccess: () => {
            showToast(t('replySubmitted') || 'Reply submitted successfully!', 'success')
            replyForm.value.message = ''
            selectedFiles.value = []
            replyForm.value.attachments = []
            if (fileInput.value) {
                fileInput.value.value = ''
            }
        },
        onError: (errors) => {
            replyForm.value.errors = errors
            showToast(t('replyFailed') || 'Failed to submit reply. Please try again.', 'error')
        },
        onFinish: () => {
            replyForm.value.processing = false
        }
    })
}

const downloadAttachment = (attachmentId) => {
    try {
        window.open(`/user/support-tickets/attachments/${attachmentId}/download`, '_blank')
        showToast(t('downloadStarted') || 'Download started', 'success')
    } catch (error) {
        showToast(t('downloadFailed') || 'Failed to download attachment', 'error')
    }
}

onMounted(() => {
    const flash = page.props.flash
    if (flash?.success) showToast(flash.success, 'success')
    if (flash?.error) showToast(flash.error, 'error')
    if (flash?.warning) showToast(flash.warning, 'warning')
    if (flash?.info) showToast(flash.info, 'info')
})
</script>

<style scoped>
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

.sts-wrap {
    max-width: 900px;
    margin: 0 auto;
    padding: 14px 12px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 14px;
    position: relative;
    z-index: 10;
    width: 100%;
}
@media(min-width:640px){ .sts-wrap{ padding: 18px 18px; } }
@media(min-width:1024px){ .sts-wrap{ padding: 20px 24px; } }

.sts-back {
    display: inline-flex; align-items: center; gap: 6px;
    background: none; border: none; cursor: pointer;
    color: rgba(255,255,255,0.4); font-size: 13px; font-weight: 600;
    padding: 4px 0; transition: color 0.2s; align-self: flex-start;
}
.sts-back:hover { color: #a75ded; }

.sts-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.13);
    border-radius: 16px; padding: 16px;
    position: relative; overflow: hidden;
}
.sts-card::before {
    content: ''; position: absolute; top: 0; left: 14%; right: 14%; height: 1px;
    background: linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}

/* header */
.sts-hdr { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.sts-hdr-main { flex: 1; min-width: 0; }
.sts-hdr-badges { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
.sts-badge { padding: 4px 10px; border-radius: 7px; font-size: 11px; font-weight: 700; }
.sts-subject { font-size: 17px; font-weight: 800; color: #fff; margin-bottom: 8px; }
.sts-meta { display: flex; flex-wrap: wrap; gap: 12px; }
.sts-meta-item { display: flex; align-items: center; gap: 4px; font-size: 11px; color: rgba(255,255,255,0.4); }
.sts-assigned {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 16px;
    background: rgba(10,4,22,0.5);
    border: 1px solid rgba(167,93,237,0.12);
    border-radius: 12px; flex-shrink: 0;
}
.sts-assigned-avatar {
    width: 36px; height: 36px; border-radius: 50%;
    background: linear-gradient(135deg, #a75ded, #7c3aed);
    display: flex; align-items: center; justify-content: center;
    font-size: 15px; font-weight: 700; color: #fff;
}
.sts-assigned-label { font-size: 10px; font-weight: 600; color: rgba(167,93,237,0.7); text-transform: uppercase; }
.sts-assigned-name { font-size: 13px; font-weight: 700; color: #fff; }

/* description */
.sts-section-title { font-size: 13px; font-weight: 800; letter-spacing: -0.01em; color: #fff; margin-bottom: 12px; }
.sts-desc-text { font-size: 13px; color: rgba(255,255,255,0.65); line-height: 1.7; white-space: pre-wrap; }
.sts-attachments { margin-top: 16px; }
.sts-attach-label { font-size: 11px; font-weight: 600; color: rgba(167,93,237,0.7); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 10px; }
.sts-attach-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px,1fr)); gap: 8px; }
.sts-attach-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px;
    background: rgba(10,4,22,0.5);
    border: 1px solid rgba(167,93,237,0.1);
    border-radius: 10px; cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
}
.sts-attach-item:hover { background: rgba(167,93,237,0.06); border-color: rgba(167,93,237,0.25); }
.sts-attach-item--sm { grid-column: auto; }
.sts-attach-icon {
    width: 32px; height: 32px; border-radius: 8px;
    background: rgba(167,93,237,0.1);
    display: flex; align-items: center; justify-content: center;
    color: #a75ded; flex-shrink: 0;
}
.sts-attach-icon--sm { width: 26px; height: 26px; border-radius: 6px; }
.sts-attach-info { flex: 1; min-width: 0; }
.sts-attach-name { font-size: 12px; font-weight: 600; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sts-attach-size { font-size: 10px; color: rgba(255,255,255,0.35); }

/* conversation */
.sts-conv-head { font-size: 13px; font-weight: 800; letter-spacing: -0.01em; color: #fff; padding: 0 2px; }

.sts-reply {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.12);
    border-radius: 14px; padding: 14px 16px;
    position: relative; overflow: hidden;
}
.sts-reply::before {
    content: ''; position: absolute; top: 0; left: 14%; right: 14%; height: 1px;
    background: linear-gradient(90deg,transparent,rgba(167,93,237,0.2),transparent);
}
.sts-reply--admin { border-left: 3px solid rgba(167,93,237,0.6); }
.sts-reply-hdr { display: flex; gap: 14px; }
.sts-reply-avatar {
    width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 700; color: #fff;
}
.sts-reply-meta { flex: 1; min-width: 0; }
.sts-reply-name-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.sts-reply-name { font-size: 14px; font-weight: 700; color: #fff; }
.sts-support-tag {
    padding: 2px 8px; border-radius: 6px; font-size: 10px; font-weight: 700;
    background: rgba(167,93,237,0.15); color: #a75ded;
}
.sts-reply-time { font-size: 11px; color: rgba(255,255,255,0.3); }
.sts-reply-msg { font-size: 13px; color: rgba(255,255,255,0.65); line-height: 1.7; white-space: pre-wrap; }
.sts-reply-attach { margin-top: 12px; display: grid; grid-template-columns: repeat(auto-fill, minmax(180px,1fr)); gap: 6px; }

/* form */
.sts-form { display: flex; flex-direction: column; gap: 14px; }
.sts-field { display: flex; flex-direction: column; gap: 0; }
.sts-field-label { font-size: 10px; font-weight: 700; color: rgba(167,93,237,0.8); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; }
.sts-input {
    width: 100%; box-sizing: border-box; padding: 9px 12px;
    background: rgba(10,4,22,0.7); border: 1px solid rgba(167,93,237,0.2);
    border-radius: 9px; color: #fff; font-size: 13px; font-weight: 500; outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
}
.sts-input::placeholder { color: rgba(255,255,255,0.22); }
.sts-input:focus { border-color: rgba(167,93,237,0.55); box-shadow: 0 0 0 3px rgba(167,93,237,0.1); }
.sts-input--err { border-color: rgba(248,113,113,0.5); }
.sts-textarea { resize: none; line-height: 1.6; }
.sts-err { font-size: 11px; color: #f87171; margin-top: 4px; }
.sts-hidden { display: none; }
.sts-attach-btn {
    display: flex; align-items: center; justify-content: center; gap: 7px;
    width: 100%; padding: 9px 14px;
    background: rgba(10,4,22,0.6); border: 1px solid rgba(167,93,237,0.2);
    border-radius: 9px; color: rgba(255,255,255,0.5); font-size: 12px; font-weight: 600;
    cursor: pointer; transition: color 0.2s, background 0.2s;
}
.sts-attach-btn:hover { color: #fff; background: rgba(167,93,237,0.08); }
.sts-files { margin-top: 8px; display: flex; flex-direction: column; gap: 5px; }
.sts-file-item { display: flex; align-items: center; gap: 9px; padding: 7px 10px; background: rgba(10,4,22,0.5); border: 1px solid rgba(167,93,237,0.08); border-radius: 7px; }
.sts-file-info { flex: 1; min-width: 0; }
.sts-file-name { font-size: 12px; font-weight: 600; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sts-file-size { font-size: 10px; color: rgba(255,255,255,0.35); }
.sts-file-remove { background: none; border: none; cursor: pointer; color: rgba(255,255,255,0.3); padding: 2px; transition: color 0.2s; }
.sts-file-remove:hover { color: #f87171; }
.sts-submit {
    padding: 10px 18px; border-radius: 9px; border: none; cursor: pointer;
    background: linear-gradient(135deg,#a75ded,#7c3aed); color: #fff; font-size: 13px; font-weight: 700;
    transition: opacity 0.2s, transform 0.15s;
}
.sts-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.sts-submit:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

/* empty / closed */
.sts-empty, .sts-closed { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 32px 16px; text-align: center; }
.sts-empty-icon { width: 56px; height: 56px; border-radius: 50%; background: rgba(167,93,237,0.08); border: 1px solid rgba(167,93,237,0.15); display: flex; align-items: center; justify-content: center; color: rgba(167,93,237,0.4); }
.sts-empty-text { font-size: 14px; color: rgba(255,255,255,0.4); }
</style>
